// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { styled } from '@mui/material/styles';
// biome-ignore lint/style/useImportType: <explanation>
import * as React from 'react';

const headerMargins = {
  marginTop: 16,
  marginBottom: 10,
};

export const Root = styled('div')(({ theme }) => ({
  ...theme.typography.body1,
  overflowWrap: 'break-word',
  '& h1': {
    ...headerMargins,
    ...theme.typography.h1,
  },
  '& h2': {
    ...headerMargins,
    ...theme.typography.h2,
  },
  '& h3': {
    ...headerMargins,
    ...theme.typography.h3,
  },
  '& h4': {
    ...headerMargins,
    ...theme.typography.h4,
  },
  '& h5': {
    ...headerMargins,
    ...theme.typography.h5,
  },
  '& h6': {
    ...headerMargins,
    ...theme.typography.h6,
  },
  '& p': {
    marginBottom: 8,
    marginTop: 8,
  },
  '& a': {
    color: theme.palette.secondary.main,
    wordBreak: 'break-all',
  },
  '& hr': {
    border: 'none',
    borderBottom: `1px solid ${theme.palette.divider}`,
    marginBottom: 8,
    marginTop: 8,
  },
  '& strong': {
    fontWeight: theme.typography.fontWeightMedium,
  },
  '& blockquote': {
    borderLeft: `5px solid ${theme.palette.divider}`,
    marginBottom: 16,
    marginLeft: 0,
    marginRight: 24,
    marginTop: 16,
    paddingLeft: 16,
  },
  '& ul': {
    marginBottom: 8,
    marginTop: 8,
    paddingLeft: 24,
  },
  '& ol': {
    marginBottom: 8,
    marginTop: 8,
    paddingLeft: 24,
  },
  '& pre': {
    marginBottom: 16,
    marginTop: 16,
    whiteSpace: 'pre-wrap',
  },
  '& table': {
    width: '100%',
    borderCollapse: 'collapse',
  },
  '& th, & td': {
    border: `1px solid ${theme.palette.divider}`,
    padding: 8,
  },
  '& img': {
    maxWidth: '80%',
    boxShadow: '0 1px 5px 1px rgba(0,0,0,.3)',
    borderRadius: '4px',
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  '& iframe': {
    maxWidth: '100%',
    boxShadow: '0 1px 5px 1px rgba(0,0,0,.3)',
    borderRadius: '4px',
  },
  // Notion blog setup converts images to <p><img/></p>
  // So this selector is for this situation to center those images
  'p > img': {
    display: 'block',
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  'p > #youtube-embed': {
    display: 'block',
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  '@media (max-width: 1200px)': {
    '& img': {
      maxWidth: '100%',
    },
  },
  '@media (max-width: 400px)': {
    '& th, & td': {
      padding: 1,
      fontSize: '0.5rem',
    },
    '& table': {
      fontSize: '0.5rem',
    },
    '& th:nth-of-type(n+7), & td:nth-of-type(n+7)': {
      display: 'none',
    },
  },
  '@media (min-width: 401px) and (max-width: 600px)': {
    '& th, & td': {
      padding: 2,
      fontSize: '0.75rem',
    },
    '& table': {
      fontSize: '0.75rem',
    },
    '& th:nth-of-type(n+7), & td:nth-of-type(n+7)': {
      display: 'none',
    },
  },
  '@media (min-width: 601px) and (max-width: 800px)': {
    '& th, & td': {
      padding: 6,
      fontSize: '0.875rem',
    },
    '& table': {
      fontSize: '0.875rem',
    },
    '& th:nth-of-type(n+8), & td:nth-of-type(n+8)': {
      display: 'none',
    },
  },
}));

interface Props {
  readonly children: React.ReactNode;
  readonly 'data-test'?: string;
}
export default function Markdown({ 'data-test': dataTest, children }: Props) {
  return <Root data-test={dataTest}>{children}</Root>;
}
